import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  globalParameterBaseConverter,
  globalParameterConverter,
  offerConverter,
} from '@seu-converter';
import { SEU_NEW_ID } from '@seu-helpers';
import {
  OfferFormService,
  RequestService,
  StoreService,
  ToastService,
} from '@seu-services';
import {
  SEU_API_ENDPOINTS,
  SeuGlobalParameter,
  SeuGlobalParameterBase,
  SeuOffer,
  SeuOfferApi,
} from '@seu-types';
import { cloneDeep } from 'lodash-es';
import { catchError, firstValueFrom, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GlobalParameterService {
  private readonly offerFormService = inject(OfferFormService);
  private readonly store = inject(StoreService);
  private readonly requestService = inject(RequestService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  public async createGlobalParameter(
    offerId: string,
    data: SeuGlobalParameterBase
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.globalParameter.replace(
      ':offerId',
      offerId
    );

    const dataApi = globalParameterBaseConverter.toApi(data);

    const responseData = await firstValueFrom(
      this.requestService
        .post<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, dataApi)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'global-parameter.toasts.create-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'global-parameter.toasts.create-success',
      // });

      if (updatedOffer.globalParameters.length === 1) {
        void this.setGlobalParametersAsDefault(
          offerId,
          updatedOffer.globalParameters[0].id
        );
      }
    }
  }

  public async updateGlobalParameters(
    offerId: string,
    globalParameterId: string,
    globalParameter: SeuGlobalParameter
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.globalParameterDetail
      .replace(':offerId', offerId)
      .replace(':globalParameterId', globalParameterId);

    const data = globalParameterConverter.toApi(globalParameter);

    const responseData = await firstValueFrom(
      this.requestService
        .put<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, data)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'global-parameter.toasts.save-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'global-parameter.toasts.save-success',
      // });
    }
  }

  public async setGlobalParametersAsDefault(
    offerId: string,
    globalParameterId: string
  ): Promise<void> {
    if (this.store.isReadonlyMode()) {
      // If the offer is in readonly mode, we only update the stored offer and do not make an API call
      const offer = await firstValueFrom(this.store.activeOffer$);
      if (!offer) {
        return;
      }
      const updatedOffer: SeuOffer = {
        ...offer,
        globalParameters:
          offer?.globalParameters.map((gp) => ({
            ...gp,
            isDefault: gp.id === globalParameterId,
          })) ?? [],
      };
      this.store.setActiveOffer(updatedOffer);
      return;
    }
    const endpoint = SEU_API_ENDPOINTS.globalParameterDefault
      .replace(':offerId', offerId)
      .replace(':globalParameterId', globalParameterId);

    const responseData = await firstValueFrom(
      this.requestService.put<null>(endpoint as SEU_API_ENDPOINTS, null).pipe(
        catchError(() => {
          this.toastService.addToast({
            type: 'error',
            text: 'global-parameter.toasts.set-default-error',
          });

          return of(null);
        })
      )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'global-parameter.toasts.set-default-success',
      // });
    }
  }

  public async duplicateGlobalParameters(
    offerId: string,
    globalParameter: SeuGlobalParameter
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.globalParameter.replace(
      ':offerId',
      offerId
    );

    const newGlobalParameter: SeuGlobalParameter = {
      ...globalParameter,
      id: '',
      isDefault: false,
      title: `${this.translateService.instant('global.copy-from')} ${globalParameter.title}`,
    };

    const data = globalParameterConverter.toApi(newGlobalParameter);

    const responseData = await firstValueFrom(
      this.requestService
        .post<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, data)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'global-parameter.toasts.duplicate-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'global-parameter.toasts.duplicate-success',
      // });
    }
  }

  public async deleteGlobalParameters(
    offerId: string,
    globalParameterId: string
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.globalParameterDetail
      .replace(':offerId', offerId)
      .replace(':globalParameterId', globalParameterId);

    const responseData = await firstValueFrom(
      this.requestService
        .delete<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'global-parameter.toasts.delete-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'global-parameter.toasts.delete-success',
      // });
    }
  }

  public initNewGlobalParameters(offer: SeuOffer): void {
    const globalParameters = cloneDeep(offer.globalParameters || []);
    const newGlobalParameter: SeuGlobalParameter = {
      id: SEU_NEW_ID,
      title: this.translateService.instant('global.untitled-global-parameters'),
      description: '',
      isDefault: false,
      inputs: [],
      version: this.store.getCurrentVersion(),
    };

    this.store.setActiveOffer({
      ...offer,
      globalParameters: [...globalParameters, newGlobalParameter],
    });
    this.offerFormService.init({
      ...offer,
      globalParameters: [...globalParameters, newGlobalParameter],
    });
  }
}
